import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  Button,
  FormField,
  FormSection,
  FormTextArea,
  IconButton,
} from '../../../components/ui';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import BusinessFunctionOrgFilter from './BusinessFunctionOrgFilter';

function ContactCard({
  index,
  contact,
  errors = {},
  canRemove,
  onChange,
  onRemove,
}) {
  const { semantic, radiusPx } = useFeatureTheme();

  return (
    <Box
      sx={{
        border: `1px solid ${semantic.borders.border}`,
        borderRadius: radiusPx.lg,
        bgcolor: semantic.surfaces.surfaceSubtle ?? semantic.surfaces.surfaceMuted,
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          Contact {index + 1}
        </Typography>
        {canRemove ? (
          <IconButton
            aria-label={`Remove contact ${index + 1}`}
            size="sm"
            onClick={() => onRemove(index)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormField
            label="Name"
            required
            value={contact.name}
            onChange={(event) => onChange(index, 'name', event.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            label="Designation"
            value={contact.designation}
            onChange={(event) => onChange(index, 'designation', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            label="Department"
            value={contact.department}
            onChange={(event) => onChange(index, 'department', event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            label="Email"
            type="email"
            value={contact.email}
            onChange={(event) => onChange(index, 'email', event.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            label="Mobile"
            required
            value={contact.mobile}
            onChange={(event) => onChange(index, 'mobile', event.target.value)}
            error={Boolean(errors.mobile)}
            helperText={errors.mobile}
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" sx={{ mt: 0.5 }}>
            <FormLabel
              component="legend"
              sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5 }}
            >
              Single point of contact?
            </FormLabel>
            <RadioGroup
              row
              value={contact.singlePointOfContact}
              onChange={(event) => onChange(index, 'singlePointOfContact', event.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function BusinessFunctionCreateForm({
  form,
  contacts,
  detailErrors,
  contactErrors,
  selectedOrganisation,
  onOrganisationChange,
  onFieldChange,
  onContactChange,
  onAddContact,
  onRemoveContact,
  submitError,
}) {
  return (
    <Stack spacing={2.5}>
      {submitError ? (
        <Typography role="alert" sx={{ color: 'error.main', fontSize: '0.875rem' }}>
          {submitError}
        </Typography>
      ) : null}

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <FormSection
              icon={<BusinessOutlinedIcon fontSize="small" />}
              title="Basic information"
              description="Core details for this business function."
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormField
                    label="Business function name"
                    required
                    value={form.name}
                    onChange={(event) => onFieldChange('name', event.target.value)}
                    error={Boolean(detailErrors.name)}
                    helperText={detailErrors.name}
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12}>
                  <BusinessFunctionOrgFilter
                    value={selectedOrganisation}
                    onChange={onOrganisationChange}
                    label="Organisation"
                    placeholder="Search and select organisation"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(event) => onFieldChange('email', event.target.value)}
                    error={Boolean(detailErrors.email)}
                    helperText={detailErrors.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    label="Phone"
                    value={form.phone}
                    onChange={(event) => onFieldChange('phone', event.target.value)}
                    error={Boolean(detailErrors.phone)}
                    helperText={detailErrors.phone}
                    slotProps={{ htmlInput: { maxLength: 13, inputMode: 'numeric' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextArea
                    label="Address"
                    minRows={2}
                    value={form.address}
                    onChange={(event) => onFieldChange('address', event.target.value)}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection
              icon={<PersonOutlineIcon fontSize="small" />}
              title="Reference & notes"
              description="Optional reference contact and internal comments."
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    label="Reference name"
                    value={form.reference_name}
                    onChange={(event) => onFieldChange('reference_name', event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    label="Reference phone"
                    value={form.reference_phone}
                    onChange={(event) => onFieldChange('reference_phone', event.target.value)}
                    error={Boolean(detailErrors.reference_phone)}
                    helperText={detailErrors.reference_phone}
                    slotProps={{ htmlInput: { maxLength: 13, inputMode: 'numeric' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    label="Reference email"
                    type="email"
                    value={form.reference_email}
                    onChange={(event) => onFieldChange('reference_email', event.target.value)}
                    error={Boolean(detailErrors.reference_email)}
                    helperText={detailErrors.reference_email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormTextArea
                    label="Comments"
                    minRows={2}
                    value={form.comments}
                    onChange={(event) => onFieldChange('comments', event.target.value)}
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={6}>
          <FormSection
            icon={<ContactPhoneOutlinedIcon fontSize="small" />}
            title="Contact persons"
            description="Add at least one contact. You can include more than one person if needed."
          >
            <Stack spacing={1.5}>
              {contacts.map((contact, index) => (
                <ContactCard
                  key={`contact-${index}`}
                  index={index}
                  contact={contact}
                  errors={contactErrors[index]}
                  canRemove={contacts.length > 1}
                  onChange={onContactChange}
                  onRemove={onRemoveContact}
                />
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<AddIcon fontSize="small" />}
                  onClick={onAddContact}
                >
                  Add another contact
                </Button>
              </Box>
            </Stack>
          </FormSection>
        </Grid>
      </Grid>
    </Stack>
  );
}
